import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { reaction } from "mobx";
import { observer } from "mobx-react-lite";

import { TRACK_NAME, useTracksHandler } from "../../hooks/useJanus/useTracksHandler";
import JanusStore from "../../stores/Janus.store";
import { useAuth } from "../authContext";

import getOldScreensArray from "./getOldScreensArray";

const JanusContext = createContext();

export const useJanusContext = () => {
  const context = useContext(JanusContext);
  if (context === undefined) throw new Error("useJanusContext must be used within a JanusContextProvider");
  return context;
};

const JanusContextProvider = ({ children }) => {
  const { userData } = useAuth();
  const { tracksHandler } = useTracksHandler();

  const [janusStore, setJanusStore] = useState(null);
  const [janusConfig, setJanusConfig] = useState();
  const [oldScreensArray, setOldScreensArray] = useState();
  const [screenStreamIds, setScreenStreamIds] = useState();
  const [MEDIA_TRACK_IDS, SET_MEDIA_TRACK_IDS] = useState();
  const [streamTracks, setStreamTracks] = useState([]);
  const [inRoomParticipants, setInRoomParticipants] = useState([]);

  useEffect(() => {
    if (janusConfig && userData.email) {
      const store = new JanusStore(janusConfig, tracksHandler, userData.email, oldScreensArray);
      setJanusStore(store);

      return () => {
        store.cleanup();
        setJanusStore(null);
      };
    }
  }, [janusConfig]);

  useEffect(() => {
    if (!janusStore) return undefined;

    const disposer = reaction(
      () => ({
        streamTrackList: janusStore.streamingStore?.streamTracks,
        participants: janusStore.videoRoomStore?.participants,
      }),
      ({ streamTrackList, participants }) => {
        setStreamTracks(streamTrackList);
        setInRoomParticipants(participants);
      },
    );

    return () => {
      disposer();
    };
  }, [janusStore]);

  useEffect(() => {
    if (janusStore && janusStore?.streamingStore?.streams) {
      const { streams } = janusStore.streamingStore;

      const initStream = Math.min(...oldScreensArray.map((screen) => screen.stream_id));
      const streamToWatch = streams.find((stream) => stream.id === initStream);
      if (streamToWatch) {
        janusStore.streamingStore.selectStream(streamToWatch);
        const tracks = streamToWatch && streamToWatch.media.filter((x) => x.type === "video" && x.mid !== TRACK_NAME.MEETING_CAMERAS);
        const trackIds = tracks && tracks.map((m) => m.mid);
        trackIds && SET_MEDIA_TRACK_IDS(trackIds);
      }
    }
  }, [janusStore?.streamingStore?.streams, oldScreensArray]);

  useMemo(() => {
    janusConfig && screenStreamIds && setOldScreensArray(getOldScreensArray(screenStreamIds, janusConfig.screenStreamIds));
  }, [janusConfig, screenStreamIds]);

  useEffect(() => {}, []);

  const stopJanus = useCallback(() => {
    janusStore && janusStore.cleanup();
  }, [janusStore]);

  const contextValue = useMemo(
    () => ({
      janusStore,
      setJanusConfig,
      setScreenStreamIds,
      stopJanus,
      MEDIA_TRACK_IDS,
      oldScreensArray,
      streamTracks,
      inRoomParticipants,
    }),
    [janusStore, streamTracks, inRoomParticipants],
  );

  return <JanusContext.Provider value={contextValue}>{children}</JanusContext.Provider>;
};

export default observer(JanusContextProvider);
